import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import SEO from "../../../components/common/SEO";
import confirmDeletion from "../../utils/confirmDeletion";

function ListingDoctors() {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const navigate = useNavigate();

    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;

    const fetchDoctors = useCallback(async () => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`${API_BASE_URL}/doctors`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setDoctors(response.data.data);
        } catch (err) {
            console.error("Error:", err);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL]);

    useEffect(() => {
        fetchDoctors();
    }, [fetchDoctors]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const handleDelete = async (id) => {
        if (!(await confirmDeletion())) return;

        try {
            const token = localStorage.getItem("authToken");
            await axios.delete(`${API_BASE_URL}/doctors/delete/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
            });

            setDoctors((prev) => prev.filter((item) => item.id !== id));
        } catch (err) {
            console.error("Delete error:", err);
        }
    };


    const filteredDoctors = doctors.filter((d) =>
        d.doctor_name.toLowerCase().includes(search.toLowerCase())
    );
    const totalPages = Math.max(1, Math.ceil(filteredDoctors.length / pageSize));
    const paginatedDoctors = filteredDoctors.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const renderStatusBadge = (status, reason) => {
        let badgeClass = 'bg-secondary';
        if (status === 'Published') badgeClass = 'bg-success';
        else if (status === 'Under Review') badgeClass = 'bg-warning';
        else if (status === 'Rejected') badgeClass = 'bg-danger';

        return (
            <div>
                <span className={`badge ${badgeClass}`}>{status || 'Published'}</span>
                {status === 'Rejected' && reason && (
                    <div className="text-danger mt-1" style={{ fontSize: '11px', whiteSpace: 'normal', maxWidth: '150px' }}>
                        <i className="ri-error-warning-line align-middle me-1"></i>
                        {reason}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="card">
            <SEO title="Doctors Management | Mangoloops" />
            <div className="card-header d-flex justify-content-between">
                <h4 className="mb-0">Doctors Listing</h4>
                <div className="d-flex gap-2">
                    <input
                        type="text"
                        placeholder="Search doctor..."
                        className="form-control"
                        style={{ width: 250 }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <Link to="/admin/add-doctor" className="btn btn-primary">
                        Add Doctor
                    </Link>
                </div>
            </div>

            <div className="card-body">
                <table className="table table-bordered">
                    <thead className="table-light">
                        <tr>
                            <th>#</th>
                            <th>Image</th>
                            <th>Doctor Name</th>
                            <th>Speciality</th>
                            <th>City</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="7" className="text-center">
                                    Loading...
                                </td>
                            </tr>
                        ) : filteredDoctors.length > 0 ? (
                            paginatedDoctors.map((doc, index) => (
                                <tr key={doc.id}>
                                    <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                    <td>
                                        {doc.doctor_images?.length > 0 ? (
                                            <img
                                                src={doc.doctor_images[0].image_url}
                                                alt=""
                                                style={{
                                                    width: 60,
                                                    height: 60,
                                                    objectFit: "cover",
                                                    borderRadius: 4,
                                                }}
                                            />
                                        ) : (
                                            "No Image"
                                        )}
                                    </td>

                                    <td>
                                        <span
                                            style={{ color: "#0d6efd", cursor: "pointer" }}
                                            onClick={() =>
                                                navigate(`/admin/edit-doctor/${doc.id}`)
                                            }
                                        >
                                            {doc.doctor_name}
                                        </span>
                                    </td>

                                    <td>
                                        {doc.sub_speciality?.sub_speciality_name || "N/A"}
                                    </td>

                                    <td>{doc.city}</td>

                                    <td>
                                        {renderStatusBadge(doc.status, doc.rejection_reason)}
                                    </td>

                                    <td>
                                        <button
                                            onClick={() => handleDelete(doc.id)}
                                            className="btn btn-sm btn-danger"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="text-center">
                                    No doctors found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {!loading && filteredDoctors.length > 0 && (
                    <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
                        <div className="text-muted">
                            Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredDoctors.length)} of {filteredDoctors.length} results
                        </div>
                        <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>
                                    Previous
                                </button>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => (
                                <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                                        {i + 1}
                                    </button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}>
                                    Next
                                </button>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ListingDoctors;
